import dayjs from 'dayjs'
import { groupBy } from 'es-toolkit'
import { NotebookPen, Pencil } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import { getEpisodeProgress } from '@/api/api'
import type { Tag } from '@/api/type'
import { DescriptionView } from '@/components/shared/description-view'
import { ReadHistoryCollapsible } from '@/components/shared/read-history-collapsible'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import { parseUtcDate } from '@/lib/date-utils'
import { formatBytes } from '@/lib/format-utils'
import { formatTime, isDurationMime } from '@/lib/time-format'
import { bookNotePath } from '@/routes/paths'
import { useAppStore } from '@/store/useAppStore'

import { TagEditor } from './tag-editor'

type Props = {
  isOpen: boolean
  onClose: () => void
  numPages?: number
  /** エピソードのMIMEタイプ。video/* または audio/* の場合は numPages を再生時間(秒)として表示 */
  mimeType?: string | null
  /** ファイルサイズ（バイト） */
  fileSize?: number | null
  tags: Tag[]
  title: string
  uploadDate: string
  description?: string
  domain?: string
  productId?: number
  episodeId?: number
  onTagsUpdated?: (tags: Tag[]) => void
}

export const InfoContainer = (props: Props) => {
  const navigate = useNavigate()
  const { domain, productId, episodeId, onTagsUpdated } = props
  const hasFullAccess = useAppStore((state) => state.hasFullAccess)
  const canUseNotes = useAppStore((state) => state.hasFullAccess || state.features.includes('memo'))
  const [isEditing, setIsEditing] = useState(false)
  const [playCount, setPlayCount] = useState<number | null>(null)

  // ダイアログが開かれた時にplay_countを取得
  useEffect(() => {
    if (!props.isOpen || !episodeId) {
      setPlayCount(null)
      return
    }
    getEpisodeProgress(episodeId)
      .then((progress) => setPlayCount(progress?.play_count ?? null))
      .catch(() => setPlayCount(null))
  }, [props.isOpen, episodeId])

  const tags = useMemo(() => {
    const groupedTags = groupBy(props.tags, (o) => o.type)

    return Object.keys(groupedTags)
      .filter((key) => key !== 'language')
      .slice()
      .sort()
      .map((key) => {
        return (
          <div key={`tag-key-${key}`} className="py-1! flex">
            <div className="me-1!">{key}:</div>
            <div className="flex gap-2 flex-wrap">
              {groupedTags[key].map((tag) => (
                <div
                  key={`tag-id-${tag.id}`}
                  className="bg-gray-600 rounded-full px-2! text-nowrap"
                  onClick={() => {
                    navigate(`/p?domain=${domain}&tags=${tag.id}`)
                    props.onClose()
                  }}
                >
                  {tag.name}
                </div>
              ))}
            </div>
          </div>
        )
      })
  }, [domain, navigate, props])

  const date = useMemo(() => {
    if (!props.uploadDate) return
    const parsed = parseUtcDate(props.uploadDate)
    if (Number.isNaN(parsed.getTime())) return
    return dayjs(parsed).format('YYYY/MM/DD HH:mm')
  }, [props.uploadDate])

  const releaseDate = useMemo(() => {
    if (!props.uploadDate) return
    const parsed = parseUtcDate(props.uploadDate)
    if (Number.isNaN(parsed.getTime())) return
    return dayjs(parsed).format('YYYY/MM/DD')
  }, [props.uploadDate])

  const isLocal = domain === 'local'

  // 編集モードを閉じる際にリセット
  const handleClose = () => {
    setIsEditing(false)
    props.onClose()
  }

  return (
    <BottomSheet open={props.isOpen} onOpenChange={(open) => !open && handleClose()} title="Info">
      <div className="m-4! overflow-auto">
        <div className="font-bold text-xl">{props.title}</div>
        {canUseNotes && productId && (
          <Button
            variant="outline"
            size="sm"
            className="mt-2!"
            onClick={() => {
              props.onClose()
              navigate(bookNotePath(productId))
            }}
          >
            <NotebookPen className="size-4" />
            読書ノート
          </Button>
        )}
        {props.description && <DescriptionView description={props.description} className="my-3!" />}

        {/* タグセクション */}
        <div className="my-4!">
          <div className="flex items-center justify-between mb-2!">
            <span className="font-semibold">タグ</span>
            {hasFullAccess && productId && domain && (
              <Button variant="ghost" size="sm" onClick={() => setIsEditing(!isEditing)}>
                <Pencil className="size-4 mr-1!" />
                {isEditing ? '完了' : '編集'}
              </Button>
            )}
          </div>

          {isEditing && productId && domain && onTagsUpdated ? (
            <TagEditor productId={productId} domain={domain} currentTags={props.tags} onTagsUpdated={onTagsUpdated} />
          ) : (
            tags.length > 0 && <div className="m-2!">{tags}</div>
          )}
        </div>

        {props.numPages ? (
          isDurationMime(props.mimeType) ? (
            <div>duration: {formatTime(props.numPages)}</div>
          ) : (
            <div>pages: {props.numPages}</div>
          )
        ) : null}
        {props.fileSize != null && <div>file size: {formatBytes(props.fileSize)}</div>}
        {isLocal ? releaseDate && <div>発売日: {releaseDate}</div> : date && <div>upload date: {date}</div>}
        {playCount != null && playCount > 0 && <div>play count: {playCount}回</div>}

        {/* 読書履歴セクション */}
        {episodeId && (
          <div className="my-4!">
            <ReadHistoryCollapsible episodeId={episodeId} />
          </div>
        )}

        <div className="h-[30vh]" />
      </div>
    </BottomSheet>
  )
}
